// MessageText.jsx

import React, { useMemo } from 'react';

// Utilities
import {
  buildMentionDisplayNames,
  splitMessageTextByMentions,
} from 'channels/utils/channelMentions';
import { buildNodeMentionDisplayTitles } from 'channels/utils/channelNodeMentions';

// CSS
import './MessageText.css';

/**
 * MessageText
 *
 * Renders message body text with highlighted @people and #node mentions.
 *
 * @returns {ReactNode} The rendered component.
 */
const MessageText = React.memo(({
  text,
  mentionKeys = [],
  membersByKey = {},
  mentionedPeopleByKey = {},
  attachments = [],
}) => {

  // ----- MEMOS ----- //

  const segments = useMemo(() => {
    const mentionNames = buildMentionDisplayNames(
      mentionKeys,
      membersByKey,
      mentionedPeopleByKey,
    );
    const nodeTitles = buildNodeMentionDisplayTitles(attachments);
    return splitMessageTextByMentions(text, mentionNames, nodeTitles);
  }, [attachments, mentionKeys, membersByKey, mentionedPeopleByKey, text]);

  // ----- RENDER ----- //

  return (
    <div className="message-text">
      {segments.map((segment, index) => {
        if (segment.type === 'mention' || segment.type === 'node_mention') {
          return (
            <span
              className={
                segment.type === 'node_mention'
                  ? 'message-text-mention message-text-node-mention'
                  : 'message-text-mention'
              }
              key={`mention-${index}`}
            >
              {segment.value}
            </span>
          );
        }

        return (
          <React.Fragment key={`text-${index}`}>
            {segment.value}
          </React.Fragment>
        );
      })}
    </div>
  );
});

// Dev Tools
MessageText.displayName = 'MessageText';

export default MessageText;
